import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as React from "react";

import {cn} from "@/lib/utils";

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    variant?: "linear" | "circular" | "continuous-circular";
    radius?: number;
  }
>(({className, value, variant = "linear", radius = 25, ...props}, ref) => {
  const circleRadius = radius;
  const circleCircumference = 2 * Math.PI * circleRadius;

  if (variant === "linear") {
    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(
          "relative h-4 w-full overflow-hidden rounded-full bg-primary",
          className
        )}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className="h-full w-full flex-1 bg-secondary transition-all"
          style={{transform: `translateX(-${100 - (value || 0)}%)`}}
        />
      </ProgressPrimitive.Root>
    );
  }

  if (variant === "circular") {
    return (
      <div className={cn("relative", className)} {...props}>
        <svg className="w-24 h-24">
          <circle
            className="text-primary"
            strokeWidth="3"
            stroke="currentColor"
            fill="transparent"
            r={circleRadius}
            cx="48"
            cy="48"
          />
          <circle
            className="text-secondary"
            strokeWidth="3"
            strokeDasharray={circleCircumference}
            strokeDashoffset={
              circleCircumference - ((value || 0) / 100) * circleCircumference
            }
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r={circleRadius}
            cx="48"
            cy="48"
          />
        </svg>
        <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-primary font-bold">
          {Math.round(value || 0)}%
        </span>
      </div>
    );
  }

  // Continuous circular variant
  return (
    <div className={cn("relative", className)} {...props}>
      <svg className="w-24 h-24 animate-spin">
        <circle
          className="text-primary"
          strokeWidth="3"
          stroke="currentColor"
          fill="transparent"
          r={circleRadius}
          cx="48"
          cy="48"
        />
        <circle
          className="text-secondary"
          strokeWidth="3"
          strokeDasharray={circleCircumference}
          strokeDashoffset={circleCircumference * 0.65}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={circleRadius}
          cx="48"
          cy="48"
        />
      </svg>
    </div>
  );
});
Progress.displayName = ProgressPrimitive.Root.displayName;

export {Progress};
